import React, { FC, useCallback, useContext, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { ProductOverviewLatestVersion, ProductOverviewReleases } from '../components/UserOverview/ProductOverview';
import { Nothing } from '../components/Nothing/Nothing';
import { createFetcher } from '../contexts/Fetcher';
import { apiOverview } from '../resources/api/apiOverview';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { PermissionContext } from '../contexts/PermissionContext';
import { EOrganizationRole, organizationRoles } from '../const/roles';
import { AuthenticatedRoutes } from '../routers/AuthenticatedRoutes';
import { ProductContent } from '../components/UserOverview/ProductContent';
import { ProductLicensesOverview } from '../components/UserOverview/ProductLicensesOverview';
import Grid from '@mui/material/Grid';
import { CustomerOverview } from '../components/CustomerOverview/CustomerOverview';
import { ResourceDenied } from '../resources/resource';
import { ProductSummaryFetcher } from '../fetchers/ProductSummaryFetcher';
import { ActiveProductContext } from '../contexts/ActiveProductContext';
import { DiscoveryOverview } from '../components/DiscoveryOverview/DiscoveryOverview';

const {
  useGetProductsList,
  useListUserProducts,
  useGetProductExamples,
  useGetSummaryUserLicenses,
  useGetSummaryMyLicenses,
  useGetOrganizationSummary,
} = apiOverview;

interface IUrlParams {
  product?: string;
}

const ProductsFetcher = createFetcher(() => {
  const { organization } = useContext(OrganizationContext);
  const { roles } = useContext(PermissionContext);

  const getProductsList = useGetProductsList(organization ?? void 0).execute;
  const listUserProducts = useListUserProducts(organization ?? void 0).execute;

  const isMember = roles.some(
    (role) => role.id === organizationRoles.find((r) => r.role === EOrganizationRole.Member)?.id
  );
  const isAdmin = roles.some(
    (role) => role.id === organizationRoles.find((r) => r.role === EOrganizationRole.Admin)?.id
  );

  return isMember && !isAdmin ? listUserProducts : getProductsList;
});

const ProductContentFetcher = createFetcher(() => {
  const { organization } = useContext(OrganizationContext);
  const product = useContext(ActiveProductContext);
  return useGetProductExamples(organization ?? void 0, product?.id).execute;
});

const ProductLicensesFetcher = createFetcher(() => {
  const { organization } = useContext(OrganizationContext);
  const product = useContext(ActiveProductContext);
  const { roles } = useContext(PermissionContext);

  const getSummaryUserLicenses = useGetSummaryUserLicenses(organization ?? void 0, product?.id).execute;
  const getSummaryMyLicenses = useGetSummaryMyLicenses(organization ?? void 0, product?.id).execute;

  const isMember = roles.some(
    (role) => role.id === organizationRoles.find((r) => r.role === EOrganizationRole.Member)?.id
  );
  const isAdmin = roles.some(
    (role) => role.id === organizationRoles.find((r) => r.role === EOrganizationRole.Admin)?.id
  );

  const execute = isMember && !isAdmin ? getSummaryMyLicenses : getSummaryUserLicenses;

  /** @todo This is just temporarily accepting result as array and as object. Eventually array is not expected here */
  return useCallback<typeof execute>(async () => {
    const data = await execute();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return Array.isArray(data) ? data[0] : data;
  }, [execute]);
  // return useGetSummaryUserLicenses(organization ?? void 0, product?.id).execute;
});

const OverviewSummary: FC = () => {
  const { data } = useContext(ProductSummaryFetcher.Context);
  const product = useContext(ActiveProductContext);
  if (!data || !product) return null;

  return (
    <>
      {product.name.toLowerCase() === 'discovery' && <DiscoveryOverview />}
      <ProductOverviewLatestVersion product={product} latest={data} xs={12} lg={4} />
      {product.name.toLowerCase() !== 'discovery' && (
        <ProductOverviewReleases product={product} latest={data} xs={12} lg={4} />
      )}
    </>
  );
};

const OverviewLicenses: FC = () => {
  const { data } = useContext(ProductLicensesFetcher.Context);

  if (!data) return null;
  return <ProductLicensesOverview licenses={data} />;
};

const OverviewContent: FC = () => {
  const { data } = useContext(ProductContentFetcher.Context);
  const product = useContext(ActiveProductContext);
  if (!data || !product) return null;

  return <ProductContent product={product} contents={data.userExamples} />;
};

const CurrentOrganizationOverviewComponent: FC = () => {
  const { data: products } = useContext(ProductsFetcher.Context);

  const { product: activeProductName } = useParams<IUrlParams>();
  const history = useHistory();

  const activeProduct = products?.products.find(({ product }) => product.name === activeProductName);

  const setActiveTab = useCallback((tab, replaceState = false) => {
    if (replaceState) {
      history.replace(AuthenticatedRoutes.CurrentOrganizationOverviewProduct(tab));
    } else {
      history.push(AuthenticatedRoutes.CurrentOrganizationOverviewProduct(tab));
    }
  }, []);

  const onChange = useCallback((event, tab) => {
    setActiveTab(tab);
  }, []);

  useEffect(() => {
    if (!activeProductName && products?.products.length) {
      setActiveTab(products.products[0].product.name, true);
    }
  }, [products, activeProductName]);

  return products?.products.length ?? 0 > 0 ? (
    <div>
      {activeProductName ? (
        <Tabs
          value={activeProductName}
          indicatorColor="primary"
          textColor="primary"
          onChange={onChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {products!.products.map((overviewProduct) => {
            return (
              <Tab
                key={overviewProduct.product.id}
                label={overviewProduct.product.name}
                value={overviewProduct.product.name}
              />
            );
          })}
        </Tabs>
      ) : null}
      {activeProduct ? (
        <ActiveProductContext.Provider value={activeProduct.product}>
          <ProductSummaryFetcher.WAF>
            <ProductLicensesFetcher.WAF>
              <Grid container spacing={3}>
                <OverviewSummary />
                {activeProduct.product.name.toLowerCase() !== 'discovery' && <OverviewLicenses />}
              </Grid>
            </ProductLicensesFetcher.WAF>
          </ProductSummaryFetcher.WAF>
          {activeProduct.hasExamples ? (
            <ProductContentFetcher.WAF>
              <OverviewContent />
            </ProductContentFetcher.WAF>
          ) : null}
        </ActiveProductContext.Provider>
      ) : null}
    </div>
  ) : products ? (
    <Nothing organizationContext={true} />
  ) : null;
};

export const CurrentOrganizationOverviewPage: FC = () => {
  const { organization } = useContext(OrganizationContext);
  const { roles } = useContext(PermissionContext);

  const isMember = roles.some(
    (role) => role.id === organizationRoles.find((r) => r.role === EOrganizationRole.Member)?.id
  );
  const isAdmin = roles.some(
    (role) => role.id === organizationRoles.find((r) => r.role === EOrganizationRole.Admin)?.id
  );
  const isOnlyMember = isMember && !isAdmin;

  const { Guard: GuardOrganizationSummary } = useGetOrganizationSummary(organization ?? void 0);
  const { Guard: GuardProductsSummaryBase } = useGetProductsList(organization ?? void 0);
  const { Guard: GuardListUserProducts } = useListUserProducts(organization ?? void 0);

  const GuardProductsSummary = isOnlyMember ? GuardListUserProducts : GuardProductsSummaryBase;

  return (
    <>
      <GuardOrganizationSummary>
        <CustomerOverview />

        <ResourceDenied>
          <GuardProductsSummary>
            <ProductsFetcher.WAF>
              <CurrentOrganizationOverviewComponent />
            </ProductsFetcher.WAF>
          </GuardProductsSummary>
        </ResourceDenied>
      </GuardOrganizationSummary>
    </>
  );
};
